import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { startRegistration } from "@simplewebauthn/browser";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Fingerprint, Trash2 } from "lucide-react";

function usePasskeys(api) {
  const [passkeys, setPasskeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${api}/auth/passkeys`, { withCredentials: true });
      setPasskeys(r.data);
    } catch {
      toast.error("Failed to load passkeys");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const add = async (label) => {
    setBusy(true);
    try {
      const r = await axios.post(`${api}/auth/passkeys/register/options`, {}, { withCredentials: true });
      const credential = await startRegistration({ optionsJSON: r.data.options });
      await axios.post(
        `${api}/auth/passkeys/register/verify`,
        { state: r.data.state, credential, label: label.trim() || undefined },
        { withCredentials: true }
      );
      toast.success("Passkey added — you can now sign in with it");
      load();
      return true;
    } catch (err) {
      if (err?.name === "NotAllowedError") {
        toast.info("Passkey creation cancelled");
      } else {
        const d = err?.response?.data?.detail;
        toast.error(typeof d === "string" ? d : "Could not add passkey");
      }
      return false;
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${api}/auth/passkeys/${encodeURIComponent(id)}`, { withCredentials: true });
      toast.success("Passkey removed");
      load();
    } catch {
      toast.error("Failed to remove passkey");
    }
  };

  return { passkeys, loading, busy, add, remove };
}

function PasskeyList({ passkeys, onRemove }) {
  return (
    <ul className="divide-y divide-slate-100" data-testid="passkeys-list">
      {passkeys.map((p) => (
        <li key={p.credential_id} className="py-3 flex items-center justify-between gap-3" data-testid={`passkey-row-${p.credential_id.slice(0, 8)}`}>
          <div>
            <div className="text-sm font-medium">{p.device_label || "Passkey"}</div>
            <div className="text-xs text-slate-500 font-mono">
              Added {p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}
              {p.last_used_at ? ` · Last used ${new Date(p.last_used_at).toLocaleDateString()}` : ""}
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => onRemove(p.credential_id)} data-testid={`passkey-delete-${p.credential_id.slice(0, 8)}`} className="text-red-600 border-red-200 hover:bg-red-50">
            <Trash2 className="w-4 h-4 mr-1.5" />Remove
          </Button>
        </li>
      ))}
    </ul>
  );
}

function PasskeyListBody({ loading, passkeys, onRemove }) {
  if (loading) return <p className="text-sm text-slate-500">Loading…</p>;
  if (passkeys.length === 0) return <p className="text-sm text-slate-500" data-testid="passkeys-empty">No passkeys registered yet.</p>;
  return <PasskeyList passkeys={passkeys} onRemove={onRemove} />;
}

export default function PasskeysPanel({ api }) {
  const [label, setLabel] = useState("");
  const { passkeys, loading, busy, add, remove } = usePasskeys(api);
  const supported = typeof window !== "undefined" && !!window.PublicKeyCredential;

  const onAdd = async () => {
    const ok = await add(label);
    if (ok) setLabel("");
  };

  return (
    <Card className="max-w-2xl border-slate-200" data-testid="passkeys-panel">
      <CardHeader className="border-b border-slate-100">
        <CardTitle className="text-lg tracking-tight flex items-center gap-2">
          <Fingerprint className="w-5 h-5 text-amber-600" />Passkeys
        </CardTitle>
        <p className="text-sm text-slate-500">
          Sign in without a password using Windows Hello (face, fingerprint or PIN), Touch ID, or a security key.
          Passkeys are stored on your device and only work on this website. Password sign-in remains available.
        </p>
      </CardHeader>
      <CardContent className="p-6 space-y-5">
        {!supported && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="passkeys-unsupported">
            This browser does not support passkeys. Use Edge or Chrome on Windows 11.
          </p>
        )}
        <PasskeyListBody loading={loading} passkeys={passkeys} onRemove={remove} />
        <div className="flex gap-2">
          <Input placeholder="Device name (e.g. Front-desk laptop)" value={label} onChange={(e) => setLabel(e.target.value)} className="bg-white max-w-xs" data-testid="passkey-label-input" />
          <Button onClick={onAdd} disabled={busy || !supported} className="bg-[#478261] hover:bg-[#3a6b51] text-white" data-testid="passkey-add-button">
            <Fingerprint className="w-4 h-4 mr-2" />{busy ? "Follow the prompt…" : "Add Passkey"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
